// Public surface of the screens module (J9) + the route manifest the shell
// (App.tsx, orchestrator-owned) wires up. Every screen assumes it is rendered
// inside <AccountProvider> (J7) and <ScreensProvider> (this module).

import { AddFriendScreen } from './addFriend/AddFriendScreen.js';
import { MainScreen } from './home/MainScreen.js';
import { ContactsScreen } from './contacts/ContactsScreen.js';
import { ContactDetailScreen } from './contacts/ContactDetailScreen.js';
import { InCallScreen } from './call/InCallScreen.js';
import { IncomingCallScreen } from './call/IncomingCallScreen.js';
import { SettingsScreen } from './settings/SettingsScreen.js';
import { AccountSettingsScreen } from './settings/AccountSettingsScreen.js';
import { SecuritySettingsScreen } from './settings/SecuritySettingsScreen.js';
import { NotificationsSettingsScreen } from './settings/NotificationsSettingsScreen.js';
import { SessionsSettingsScreen } from './settings/SessionsSettingsScreen.js';
import { LanguageSettingsScreen } from './settings/LanguageSettingsScreen.js';
import { CallsSettingsScreen } from './settings/CallsSettingsScreen.js';
import { AndroidSettingsScreen } from './settings/AndroidSettingsScreen.js';
import { DeveloperScreen } from './settings/DeveloperScreen.js';

export {
  AccountSettingsScreen,
  SecuritySettingsScreen,
  AddFriendScreen,
  AndroidSettingsScreen,
  ContactDetailScreen,
  ContactsScreen,
  DeveloperScreen,
  InCallScreen,
  IncomingCallScreen,
  LanguageSettingsScreen,
  MainScreen,
  NotificationsSettingsScreen,
  CallsSettingsScreen,
  SessionsSettingsScreen,
  SettingsScreen,
};
export { TabLayout } from './ui/TabLayout.js';
export { ScreensProvider, useScreens, type ScreensDeps } from './ScreensContext.js';
export { RestClient, RestError, type AccessTokenProvider, type RestClientOptions } from '@merkury/brain/api/client.js';
export { LiveEvents, type LiveEventsState } from '@merkury/brain/api/liveEvents.js';
export {
  deleteAccount,
  removeAccountPassword,
  setAccountPassword,
} from './api/credentials.js';
export type { CallEngineLike, EchoTestLike, MediaControlLike } from './call/engineTypes.js';
export { createReconnectTone, type TonePlayer } from '@merkury/brain/call/reconnectTone.js';
export { QrCode } from './qr/QrCode.js';
export { encodeQr, type EcLevel } from '@merkury/brain/lib/qrEncode.js';
export { extractUuid } from './addFriend/AddFriendScreen.js';

/**
 * Route manifest: path → screen component, all requiring an account (the
 * shell's RequireAccount gate). `tab: true` marks the three primary screens
 * that render inside the persistent bottom tab bar (Main, Contacts, Settings);
 * everything else (in-call, add-friend, contact detail, developer) renders
 * bare. `/add` must keep the URL fragment intact — the friend UUID rides in
 * `#fragment` and never reaches the network.
 */
export const SCREEN_ROUTES = [
  { path: '/', Component: MainScreen, tab: true },
  { path: '/contacts', Component: ContactsScreen, tab: true },
  { path: '/contact/:userId', Component: ContactDetailScreen },
  { path: '/add', Component: AddFriendScreen },
  { path: '/call', Component: InCallScreen },
  { path: '/incoming', Component: IncomingCallScreen },
  { path: '/settings', Component: SettingsScreen, tab: true },
  { path: '/settings/account', Component: AccountSettingsScreen },
  { path: '/settings/security', Component: SecuritySettingsScreen },
  { path: '/settings/notifications', Component: NotificationsSettingsScreen },
  { path: '/settings/sessions', Component: SessionsSettingsScreen },
  { path: '/settings/language', Component: LanguageSettingsScreen },
  { path: '/settings/calls', Component: CallsSettingsScreen },
  { path: '/settings/android', Component: AndroidSettingsScreen },
  { path: '/settings/developer', Component: DeveloperScreen },
] as const;
